import { useId, useRef, useState, type ReactNode } from 'react'
import { Button, Chip, Modal } from '@heroui/react'
import { useStaffText } from '../ui/StaffText'

/** A status tone as Reception and Appointments name it, as a HeroUI chip colour. */
export type ChipColor = 'default' | 'accent' | 'success' | 'warning' | 'danger'
const TONE: Record<string, ChipColor> = {
  ok: 'success',
  bad: 'danger',
  warn: 'warning',
  call: 'accent',
  serve: 'accent',
  early: 'accent',
  off: 'default',
  grey: 'default',
}
export const toneColor = (tone: string | undefined): ChipColor => (tone ? TONE[tone] ?? 'default' : 'default')

/** A status said as a soft chip in its tone's colour. */
export function StatusChip({ tone, children, className }: { tone?: string; children: ReactNode; className?: string }) {
  return (
    <Chip size="sm" variant="soft" color={toneColor(tone)} className={className}>
      {children}
    </Chip>
  )
}

/**
 * FormDialog's contract on a HeroUI Modal: a form that submits on Enter, Cancel and
 * the primary action in the footer, and a discard question once something was typed.
 */
export function FormModal({
  title,
  sub,
  onClose,
  onSubmit,
  submitLabel,
  valid = true,
  busy = false,
  extra,
  closeLabel,
  hideSubmit = false,
  wide = false,
  children,
}: {
  title: string
  sub?: string
  onClose: () => void
  onSubmit?: () => void
  submitLabel?: string
  valid?: boolean
  busy?: boolean
  /** Footer's left side: a secondary or destructive action. */
  extra?: ReactNode
  closeLabel?: string
  hideSubmit?: boolean
  wide?: boolean
  children: ReactNode
}) {
  const { say } = useStaffText()
  const formId = useId()
  const dirty = useRef(false)
  const [asking, setAsking] = useState(false)
  const submit = !hideSubmit && onSubmit && submitLabel
  const close = () => (dirty.current ? setAsking(true) : onClose())

  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && close()} className="rx-modal">
      <Modal.Container size={wide ? 'lg' : 'md'} scroll="inside">
        <Modal.Dialog>
          <Modal.CloseTrigger aria-label={say('ui.close')} />
          <Modal.Header className="rx-modal__head">
            <Modal.Heading>{title}</Modal.Heading>
            {sub && <p className="rx-modal__sub">{sub}</p>}
          </Modal.Header>
          <Modal.Body>
            <form
              id={formId}
              className="form-dialog"
              onSubmit={(e) => {
                e.preventDefault()
                if (valid && !busy && onSubmit) onSubmit()
              }}
              onInput={() => {
                dirty.current = true
              }}
              onChange={() => {
                dirty.current = true
              }}
            >
              {children}
            </form>
          </Modal.Body>
          <Modal.Footer className="rx-modal__foot">
            {asking ? (
              <>
                <strong className="rx-modal__ask">{say('ui.discardAsk')}</strong>
                <span className="rx-grow" />
                <Button variant="tertiary" size="sm" autoFocus onPress={() => setAsking(false)}>
                  {say('ui.keepEditing')}
                </Button>
                <Button variant="danger" size="sm" onPress={onClose}>
                  {say('ui.discard')}
                </Button>
              </>
            ) : (
              <>
                {extra && <span className="rx-modal__extra">{extra}</span>}
                <span className="rx-grow" />
                <Button variant="tertiary" size="sm" onPress={onClose}>
                  {closeLabel ?? (submit ? say('common.cancel') : say('ui.close'))}
                </Button>
                {submit && (
                  <Button type="submit" form={formId} variant="primary" size="sm" isDisabled={!valid || busy}>
                    {submitLabel}
                  </Button>
                )}
              </>
            )}
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

/** A yes-or-keep question inside a card or dialog, in place of the buttons that raised it. */
export function Confirm({
  question,
  yes,
  no,
  busy,
  onYes,
  onNo,
  children,
}: {
  question: string
  yes: string
  no: string
  busy?: boolean
  onYes: () => void
  onNo: () => void
  children?: ReactNode
}) {
  return (
    <div className="rx-confirm" role="alertdialog" aria-label={question}>
      <strong>{question}</strong>
      {children}
      <span className="rx-confirm__do">
        <Button variant="danger" size="sm" isDisabled={busy} onPress={onYes}>
          {yes}
        </Button>
        <Button variant="tertiary" size="sm" onPress={onNo}>
          {no}
        </Button>
      </span>
    </div>
  )
}
